import {
  CartItem,
  FilteredMaterialsByCategoriesI,
  IProductConfigurationResult,
  IProductMaterialFilterEvent,
  Material,
  ProductData,
} from "ecom-types";
import { ringSizeSku } from "~/configs";

export function useChangeCartItemConfiguration(product: Ref<CartItem>) {
  const { $api } = useNuxtApp();
  const generalStore = useGeneralStore();
  const { updateCartItem } = useCart();

  const productData = ref<null | ProductData>(null);
  const isEditMode = ref(false);
  const isFetching = ref(false);
  const isUpdating = ref(false);
  const currentProductConfigurationMaterials: IProductConfigurationResult =
    reactive({});
  const currentProductOptionsConfigurationMaterials: IProductConfigurationResult =
    reactive({});

  const filteredMaterialsByCategories = computed(
    (): FilteredMaterialsByCategoriesI[] => {
      if (!productData.value) {
        return [];
      }

      const materialsByCategories =
        productData.value.material_categories.reduce(
          (acc: FilteredMaterialsByCategoriesI[], el) => {
            const currentCategory =
              generalStore.bootstrap?.material_categories?.find(
                (it) => it.id === el.id,
              );

            if (currentCategory) {
              acc.push({
                ...currentCategory,
                materials: (productData.value?.materials || []).reduce(
                  (acc: Material[], material) => {
                    if (material.material_category_id === el.id) {
                      acc.push({
                        ...material,
                      });
                    }
                    return acc;
                  },
                  [],
                ),
              });
            }
            return acc;
          },
          [],
        );

      return materialsByCategories;
    },
  );

  const productMaterialsCategoriesById = computed(() => {
    return (productData.value?.material_categories || []).reduce(
      (acc, el) => {
        acc[el.id] = el;
        return acc;
      },
      {} as Record<string, ProductData["material_categories"][number]>,
    );
  });

  const currentProductConfiguration = computed(() => {
    return {
      ...currentProductConfigurationMaterials,
      ...currentProductOptionsConfigurationMaterials,
    };
  });

  async function getProductData() {
    isFetching.value = true;
    try {
      const { data } = await $api.products.getProductByIdOrSlug(
        product.value.slug as string,
      );
      productData.value = data as ProductData;
    } catch (e) {
      console.error(e);
    } finally {
      isFetching.value = false;
    }
  }

  function setCurrentProductConfiguration() {
    if (product.value && productData.value) {
      const productMaterials = productData.value.materials || [];
      product.value.materials.forEach((item) => {
        const category =
          productMaterialsCategoriesById.value?.[item.category_id];
        if (category) {
          currentProductConfigurationMaterials[category.slug] =
            productMaterials.find((material) => material.sku === item.sku) ||
            null;
        }
      });

      (product.value.options?.materials || []).forEach((item) => {
        if (item?.material_category_id) {
          const category =
            productMaterialsCategoriesById.value?.[item.material_category_id];
          if (category) {
            currentProductOptionsConfigurationMaterials[category.slug] =
              productMaterials.find((material) => material.sku === item.sku) ||
              null;
          }
        }
      });
    }
  }

  async function startEditMode() {
    if (!productData.value) {
      await getProductData();
    }
    setCurrentProductConfiguration();

    isEditMode.value = true;
  }

  async function updateItem() {
    isUpdating.value = true;
    const params: Record<string, any> = {};

    const productMaterials = product.value.materials || [];
    const productOptionsMaterials = product.value.options?.materials || [];
    const changedMaterials = productOptionsMaterials.map((item) => {
      const category =
        productMaterialsCategoriesById.value?.[item.material_category_id];
      if (
        category &&
        currentProductOptionsConfigurationMaterials[category.slug]
      ) {
        const selectedMaterial =
          currentProductOptionsConfigurationMaterials[category.slug];
        if (!selectedMaterial) return item;

        return {
          ...item,
          sku: selectedMaterial.sku,
          title: selectedMaterial.currentTranslation?.title,
          material_category_title: category.currentTranslation?.title,
          material_category_sku: category,
        };
      }
      return item;
    });

    const changedProductConfigurationMaterials = Object.values(
      currentProductConfigurationMaterials,
    ).filter((item) => {
      const oldMaterial = productMaterials.find(
        (material) => material.category_id === item?.material_category_id,
      );
      return oldMaterial?.id !== item?.id;
    });

    if (!isEqual(productOptionsMaterials, changedMaterials)) {
      params.options = {
        ...product.value.options,
        materials: changedMaterials,
      };
    }
    if (changedProductConfigurationMaterials.length) {
      params.material_id = changedProductConfigurationMaterials?.[0]?.id;
    }

    if (!isEmpty(params)) {
      await updateCartItem({
        ...product.value,
        ...params,
      });
    }

    isEditMode.value = false;
    isUpdating.value = false;
  }

  function setSelectedValue({ slug, item }: IProductMaterialFilterEvent) {
    if (currentProductConfigurationMaterials[slug]) {
      currentProductConfigurationMaterials[slug] = item || null;
    } else if (currentProductOptionsConfigurationMaterials[slug]) {
      currentProductOptionsConfigurationMaterials[slug] = item || null;
    }
  }

  return {
    getProductData,
    filteredMaterialsByCategories,
    productData,
    isEditMode,
    isFetching,
    startEditMode,
    updateItem,
    setSelectedValue,
    currentProductConfiguration,
    isUpdating,
  };
}
